import UrlPattern from 'url-pattern';

const FALLBACK_ROUTE = '*';

const URL_PATTERN_OPTIONS = { segmentValueCharset: 'a-zA-Z0-9-_~ %.' };
const matchRoute = (route, path) => new UrlPattern(route, URL_PATTERN_OPTIONS).match(path);

const decodeParams = (match) =>
  Object.keys(match).reduce((acc, cur) => {
    acc[cur] = decodeURIComponent(match[cur]);
    return acc;
  }, {});

const normalize = (pathname) => {
  if (pathname === '' || pathname === '/') {
    return '/';
  }
  return pathname.replace(/\/+$/g, '');
};

export class Router {
  static resultIdentifier = '@@RouterResult';

  constructor() {
    this.routes = new Map();
    this.preFetchResult = new Map();
    this.matchListeners = [];
  }

  add = (route, callback, options = {}) => {
    this.routes.set(route, { callback, options });
  };

  fallback = (redirect) => {
    this.routes.set(FALLBACK_ROUTE, { route: redirect, ...this.routes.get(redirect) });
  };

  onMatch = (callback) => this.matchListeners.push(callback);

  triggerMatch = (result) => this.matchListeners.forEach((cb) => cb(result));

  preFetch = async (path) => {
    const { route, pathname, params, callback, options = {} } = this.matchPath(path) || {};
    if (route && callback && options.preFetch) {
      const preFetchResult = Promise.resolve(callback({ pathname, route, params }, () => {}, { preFetch: true }));
      this.preFetchResult.set(path, { route, pathname, params, callback, preFetchResult });
    }
  };

  resolve = (path) => {
    const preFetchRoute = this.preFetchResult.get(path);
    preFetchRoute && this.preFetchResult.delete(path);
    return this.match(path, [], preFetchRoute);
  };

  match = (path, prevMatches = [], preFetchRoute) =>
    new Promise((resolve, reject) => {
      if (prevMatches.length >= 5) {
        return reject('too many redirects');
      }

      const { route, pathname, params, callback, preFetchResult } = preFetchRoute || this.matchPath(path) || {};

      if (!route) {
        reject('failed to match route');
      }

      const routeObj = {
        pathname: decodeURIComponent(pathname),
        route,
        params,
        prevMatches,
        [Router.resultIdentifier]: true,
      };

      this.triggerMatch(routeObj);
      this.resolveRoute(routeObj, callback, prevMatches, preFetchResult).then(resolve).catch(reject);
    });

  matchPath = (pathname) => {
    const normalizedPathname = normalize(pathname);

    for (let [route, { callback, options }] of this.routes) {
      const match = matchRoute(route, normalizedPathname);

      if (match) {
        if (route === FALLBACK_ROUTE) {
          ({ route, callback, options } = this.routes.get(FALLBACK_ROUTE));
        }

        return {
          pathname,
          route,
          params: decodeParams(match),
          callback,
          options,
        };
      }
    }
  };

  resolveRoute = async (routeObj, callback, prevMatches = [], preFetchResult) => {
    if (!callback) {
      return routeObj;
    }
    const redirect = (path) => this.match(path, prevMatches.concat(routeObj));
    const val = await Promise.resolve(callback(routeObj, redirect, { preFetchResult }));
    return val && val[Router.resultIdentifier] ? val : routeObj;
  };
}
